// components/AccessibleButton.tsx - Accessible button component with loading and disabled states
import React, { forwardRef } from 'react';
import LoadingSpinner from './LoadingSpinner';

interface AccessibleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  ariaLabel?: string;
  ariaPressed?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaDescribedBy?: string;
}

const AccessibleButton = forwardRef<HTMLButtonElement, AccessibleButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      loading = false,
      loadingText,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      disabled,
      className = '',
      ariaLabel,
      ariaPressed,
      ariaExpanded,
      ariaControls,
      ariaDescribedBy,
      onClick,
      ...props
    },
    ref
  ) => {
    // Size classes
    const sizeClasses = {
      small: 'px-3 py-1.5 text-sm',
      medium: 'px-4 py-2 text-base',
      large: 'px-6 py-3 text-lg',
    };

    // Variant classes
    const variantClasses = {
      primary:
        'bg-blue text-white hover:bg-blue-darker focus:ring-blue disabled:bg-grey-light dark:bg-blue dark:hover:bg-blue-darker',
      secondary:
        'bg-white text-blue border-2 border-blue hover:bg-blue-very-light focus:ring-blue disabled:border-grey-light disabled:text-grey-light dark:bg-grey-dark dark:text-blue-light dark:border-blue-light dark:hover:bg-grey-darker',
      danger:
        'bg-red-darker text-white hover:bg-[#A43D3D] focus:ring-red-darker disabled:bg-grey-light',
      ghost:
        'bg-transparent text-grey-darker border-2 border-grey-light hover:bg-grey-light hover:border-grey focus:ring-grey-light disabled:text-grey-light dark:text-grey-light dark:border-grey dark:hover:bg-grey-dark dark:hover:border-grey-light',
    };

    const baseClasses = `
      inline-flex items-center justify-center
      font-medium rounded-lg
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:cursor-not-allowed disabled:opacity-60
      ${fullWidth ? 'w-full' : ''}
      ${sizeClasses[size]}
      ${variantClasses[variant]}
      ${className}
    `;

    const isDisabled = disabled || loading;

    // Handle keyboard activation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!isDisabled && onClick) {
          onClick(e as any);
        }
      }
    };

    return (
      <button
        ref={ref}
        className={baseClasses}
        disabled={isDisabled}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel || (typeof children === 'string' ? children : undefined)}
        aria-pressed={ariaPressed}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-describedby={ariaDescribedBy}
        aria-busy={loading}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner size="small" className="mr-2" />
            <span>{loadingText || 'Loading...'}</span>
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className="mr-2" aria-hidden="true">
                {icon}
              </span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className="ml-2" aria-hidden="true">
                {icon}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);

AccessibleButton.displayName = 'AccessibleButton';

export default AccessibleButton;